
import React, { useState } from 'react';
import { Protege, SponsorshipTemplate, StepTemplateConfig } from '../types';
import { Users, AlertCircle, Share2, MessageSquare, FileText, Plus, Clock, Save, Trash2, Check, X, QrCode, Link as LinkIcon, Mail, Shield, ShieldCheck, Heart } from 'lucide-react';

interface SponsorDashboardProps {
    proteges: Protege[];
    onUpdateProtege: (updatedProtege: Protege) => void;
}

const DEFAULT_TEMPLATE_CONFIGS: StepTemplateConfig[] = Array.from({ length: 12 }, (_, i) => ({
    stepNum: i + 1,
    homework: `Read Chapter ${i + 1} and meet with sponsor.`,
    durationDays: 14 // Default 2 weeks per step
}));

export const SponsorDashboard: React.FC<SponsorDashboardProps> = ({ proteges = [], onUpdateProtege }) => {
  const [activeTab, setActiveTab] = useState<'roster' | 'templates'>('roster');
  const [templates, setTemplates] = useState<SponsorshipTemplate[]>([
      { 
          id: '1', 
          name: 'Standard Big Book Process', 
          description: 'The traditional approach. Reading the main text (pp. 1-164) with weekly meetings.', 
          configs: DEFAULT_TEMPLATE_CONFIGS 
      },
      { 
          id: '2', 
          name: 'Intensive "Back to Basics"', 
          description: 'A 4-week rapid progression through the steps, designed for newcomers in crisis.', 
          configs: DEFAULT_TEMPLATE_CONFIGS.map(c => ({...c, durationDays: 3})) 
      },
      {
          id: '3',
          name: 'Maintenance & Service',
          description: 'For those who have completed the work and are now focusing on Steps 10, 11, and 12.',
          configs: DEFAULT_TEMPLATE_CONFIGS.map((c, i) => ({...c, durationDays: i > 8 ? 30 : 7, homework: i > 8 ? 'Daily practice' : 'Review basics'}))
      }
  ]);
  
  // Template Editing State
  const [isEditingTemplate, setIsEditingTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<SponsorshipTemplate | null>(null);

  // Review State
  const [reviewingProtege, setReviewingProtege] = useState<Protege | null>(null);
  const [feedbackText, setFeedbackText] = useState('');

  // Invite Modal State
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteMethod, setInviteMethod] = useState<'qr' | 'link' | 'email'>('qr');

  // --- Template Handlers ---

  const handleStartEdit = (template?: SponsorshipTemplate) => {
      if (template) {
          setEditingTemplate({ ...template });
      } else {
          setEditingTemplate({
              id: Date.now().toString(),
              name: 'New Template',
              description: '',
              configs: Array.from({ length: 12 }, (_, i) => ({
                stepNum: i + 1,
                homework: '',
                durationDays: 7
            }))
          });
      }
      setIsEditingTemplate(true);
  };

  const handleSaveTemplate = () => {
      if (!editingTemplate) return;
      
      const exists = templates.find(t => t.id === editingTemplate.id);
      if (exists) {
          setTemplates(templates.map(t => t.id === editingTemplate.id ? editingTemplate : t));
      } else {
          setTemplates([...templates, editingTemplate]);
      }
      setIsEditingTemplate(false);
      setEditingTemplate(null);
  };

  const updateConfig = (stepNum: number, field: keyof StepTemplateConfig, value: any) => {
      if (!editingTemplate) return;
      const newConfigs = editingTemplate.configs.map(c => 
          c.stepNum === stepNum ? { ...c, [field]: value } : c
      );
      setEditingTemplate({ ...editingTemplate, configs: newConfigs });
  };

  // --- Review Handlers ---
  const handleApproveStep = () => {
      if (!reviewingProtege) return;
      
      const updated = { 
          ...reviewingProtege, 
          step: reviewingProtege.step < 12 ? reviewingProtege.step + 1 : 12,
          pendingReview: false,
          revisionRequested: false,
          status: 'green' as const 
      };
      
      onUpdateProtege(updated);
      alert(`Approved ${reviewingProtege.name}'s Step ${reviewingProtege.step}.`);
      setReviewingProtege(null);
      setFeedbackText('');
  };

  const handleRejectStep = () => {
      if (!reviewingProtege) return;
      
      // Update status to indicate revision needed
      const updated = {
          ...reviewingProtege,
          pendingReview: false,
          revisionRequested: true,
          status: 'yellow' as const // Warning state
      };

      onUpdateProtege(updated);
      alert(`Requested changes for ${reviewingProtege.name}. They will be notified to revise their work.`);
      setReviewingProtege(null);
      setFeedbackText('');
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden min-h-[600px]">
      {/* Header Tabs */}
      <div className="flex border-b border-slate-200 dark:border-slate-700">
          <button 
            onClick={() => setActiveTab('roster')}
            className={`flex-1 py-4 font-medium text-sm flex items-center justify-center gap-2 border-b-2 transition-colors ${activeTab === 'roster' ? 'border-brand-600 text-brand-600 dark:text-brand-400' : 'border-transparent text-slate-500'}`}
          >
              <Users className="w-4 h-4" /> Active Roster
          </button>
          <button 
             onClick={() => setActiveTab('templates')}
             className={`flex-1 py-4 font-medium text-sm flex items-center justify-center gap-2 border-b-2 transition-colors ${activeTab === 'templates' ? 'border-brand-600 text-brand-600 dark:text-brand-400' : 'border-transparent text-slate-500'}`}
          >
              <FileText className="w-4 h-4" /> Sponsorship Templates
          </button>
      </div>

      <div className="p-4 md:p-6">
        {/* --- ROSTER TAB --- */}
        {activeTab === 'roster' && !reviewingProtege && (
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <h3 className="font-bold dark:text-white">Your Proteges</h3>
                    {proteges.length > 0 && (
                        <button 
                            onClick={() => setShowInviteModal(true)}
                            className="text-sm bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 rounded-lg flex items-center gap-2 shadow-sm"
                        >
                            <Share2 className="w-4 h-4" /> Invite New
                        </button>
                    )}
                </div>

                {proteges.length === 0 ? (
                    // SPONSORSHIP EMPTY STATE
                    <div className="flex flex-col items-center justify-center py-12 px-4 text-center border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900/50">
                        <div className="w-16 h-16 bg-brand-100 dark:bg-brand-900/30 rounded-full flex items-center justify-center text-brand-600 dark:text-brand-400 mb-6">
                            <Shield className="w-8 h-8" />
                        </div>
                        <h4 className="text-xl font-bold dark:text-white mb-3">Sponsorship is a Privilege</h4>
                        <p className="text-sm text-slate-500 max-w-md mb-2 leading-relaxed italic">
                            "Practical experience shows that nothing will so much insure immunity from drinking as intensive work with other alcoholics."
                        </p>
                        <p className="text-xs text-slate-400 mb-8 font-medium">— Alcoholics Anonymous, p. 89</p>
                        
                        <p className="text-sm text-slate-500 max-w-md mb-8 leading-relaxed">
                            This dashboard is designed for those who have completed the 12 Steps and are ready to guide others. Once you are ready, invite a sponsee to begin their journey.
                        </p>
                        <button 
                            onClick={() => setShowInviteModal(true)}
                            className="bg-brand-600 hover:bg-brand-700 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg hover:shadow-brand-500/20 transition-all"
                        >
                            <Heart className="w-5 h-5" /> I Am Ready to Sponsor
                        </button>
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-700">
                        {proteges.map((p) => (
                        <div key={p.id} className="py-4 flex items-center justify-between group">
                            <div className="flex items-center gap-4">
                                <div className="relative">
                                    <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center font-bold text-slate-600 dark:text-slate-300 flex-shrink-0">
                                        {p.name.charAt(0)}
                                    </div>
                                    <div className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-white dark:border-slate-800 ${
                                    p.status === 'green' ? 'bg-green-500' : p.status === 'yellow' ? 'bg-amber-500' : 'bg-red-500'
                                    }`}></div>
                                </div>
                                
                                <div className="flex flex-col">
                                    <h4 className="font-medium dark:text-white text-base">
                                        {p.name}
                                    </h4>
                                    <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-3 text-xs text-slate-500">
                                        <span>Step {p.step}</span>
                                        <span className="hidden md:inline">•</span>
                                        <span>Last seen {p.lastCheckIn}</span>
                                    </div>
                                    
                                    {p.pendingReview && (
                                        <div className="mt-1">
                                            <span className="inline-flex text-[10px] bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300 px-2 py-0.5 rounded-full font-bold items-center gap-1 animate-pulse">
                                                <Clock className="w-3 h-3" /> Needs Review
                                            </span>
                                        </div>
                                    )}

                                    {p.revisionRequested && (
                                        <div className="mt-1">
                                            <span className="inline-flex text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 rounded-full font-bold items-center gap-1">
                                                <AlertCircle className="w-3 h-3" /> Changes Requested
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex gap-2">
                                {p.pendingReview ? (
                                    <button 
                                        onClick={() => setReviewingProtege(p)}
                                        className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs rounded-lg font-medium shadow-sm whitespace-nowrap"
                                    >
                                        Review
                                    </button>
                                ) : (
                                    <button className="p-2 text-slate-400 hover:text-brand-600 bg-slate-50 dark:bg-slate-700 rounded-lg">
                                        <MessageSquare className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                        ))}
                    </div>
                )}
            </div>
        )}

        {/* --- REVIEW MODE --- */}
        {activeTab === 'roster' && reviewingProtege && (
            <div className="animate-in slide-in-from-right-4">
                <div className="flex items-center gap-2 mb-6">
                    <button onClick={() => setReviewingProtege(null)} className="text-slate-400 hover:text-slate-600 text-sm">Back</button>
                    <span className="text-slate-300">/</span>
                    <span className="font-bold dark:text-white">Reviewing {reviewingProtege.name}</span>
                </div>

                <div className="bg-slate-50 dark:bg-slate-900/50 p-6 rounded-xl border border-slate-200 dark:border-slate-700 mb-6">
                    <h4 className="font-bold text-lg mb-2 dark:text-white">Step {reviewingProtege.step} Submission</h4>
                    <p className="text-sm text-slate-500 mb-4">Submitted today at 9:00 AM</p>
                    
                    <div className="bg-white dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-700 dark:text-slate-300 italic mb-6">
                        "I've completed the reading and the worksheet. I struggled a bit with the concept of 'sanity' but I think I understand it better now."
                    </div>

                    <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Sponsor Feedback</label>
                    <textarea 
                        className="w-full p-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-brand-500 h-32 mb-4 text-sm"
                        placeholder="Write your encouragement or feedback here..."
                        value={feedbackText}
                        onChange={e => setFeedbackText(e.target.value)}
                    />

                    <div className="flex gap-3">
                        <button 
                            onClick={handleApproveStep}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-xl font-bold flex justify-center items-center gap-2 shadow-sm"
                        >
                            <Check className="w-4 h-4" /> Approve & Unlock Step {reviewingProtege.step + 1}
                        </button>
                        <button 
                            onClick={handleRejectStep}
                            className="flex-1 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 py-2.5 rounded-xl font-bold flex justify-center items-center gap-2"
                        >
                            <MessageSquare className="w-4 h-4" /> Request Revision
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* --- TEMPLATES TAB --- */}
        {activeTab === 'templates' && !isEditingTemplate && (
            <div className="space-y-6">
                 {/* Updated Header with compact buttons for mobile */}
                 <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                    <div>
                        <h3 className="font-bold dark:text-white">Sponsorship Templates</h3>
                        <p className="text-sm text-slate-500">Pre-set homework and time limits.</p>
                    </div>
                    <button 
                        onClick={() => handleStartEdit()}
                        className="text-xs sm:text-sm bg-brand-600 hover:bg-brand-700 text-white px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 shadow-sm w-full sm:w-auto font-bold"
                    >
                        <Plus className="w-4 h-4" /> Create New
                    </button>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    {templates.map(t => (
                        <div key={t.id} className="p-4 border border-slate-200 dark:border-slate-700 rounded-xl hover:border-brand-400 transition-colors group relative bg-slate-50 dark:bg-slate-800/50">
                            <h4 className="font-bold dark:text-white">{t.name}</h4>
                            <p className="text-xs text-slate-500 mb-3 line-clamp-2 h-8">{t.description || 'No description'}</p>
                            <div className="flex items-center gap-4 text-xs text-slate-400">
                                <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> 12 Steps</span>
                                {/* Round to nearest whole week */}
                                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> ~{Math.round(t.configs.reduce((a,b) => a + b.durationDays, 0) / 7)} Weeks</span>
                            </div>
                            <div className="mt-4 flex gap-2">
                                <button onClick={() => handleStartEdit(t)} className="text-xs font-bold text-brand-600 hover:underline">Edit Plan</button>
                                <button className="text-xs font-bold text-slate-400 hover:text-slate-600">Duplicate</button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )}

        {/* --- TEMPLATE EDIT MODE --- */}
        {activeTab === 'templates' && isEditingTemplate && editingTemplate && (
             <div className="animate-in slide-in-from-right-4">
                 <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                    <div className="flex items-center gap-2 w-full">
                        <button onClick={() => setIsEditingTemplate(false)} className="text-slate-400 hover:text-slate-600 text-sm whitespace-nowrap">Cancel</button>
                        <span className="text-slate-300">/</span>
                        <input 
                            className="font-bold text-lg bg-transparent border-b border-transparent hover:border-slate-300 focus:border-brand-500 outline-none dark:text-white w-full"
                            value={editingTemplate.name}
                            onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})}
                        />
                    </div>
                    <button onClick={handleSaveTemplate} className="w-full sm:w-auto bg-brand-600 text-white px-4 py-2.5 rounded-lg text-xs sm:text-sm font-bold flex items-center justify-center gap-2 shadow-sm">
                        <Save className="w-4 h-4" /> Save Template
                    </button>
                 </div>

                 <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                     {editingTemplate.configs.map((config) => (
                         <div key={config.stepNum} className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-700">
                             <div className="flex justify-between items-start mb-2">
                                 <span className="text-xs font-bold uppercase tracking-wider text-slate-400 bg-white dark:bg-slate-800 px-2 py-1 rounded">Step {config.stepNum}</span>
                                 <div className="flex items-center gap-2">
                                     <label className="text-xs text-slate-500">Days allowed:</label>
                                     <input 
                                        type="number"
                                        className="w-16 p-1 text-sm border rounded text-center dark:bg-slate-800 dark:text-white dark:border-slate-600"
                                        value={config.durationDays}
                                        onChange={(e) => updateConfig(config.stepNum, 'durationDays', parseInt(e.target.value))}
                                     />
                                 </div>
                             </div>
                             <textarea 
                                className="w-full text-sm p-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 dark:text-white resize-none h-20 focus:ring-1 focus:ring-brand-500 outline-none"
                                placeholder="Assign homework for this step..."
                                value={config.homework}
                                onChange={(e) => updateConfig(config.stepNum, 'homework', e.target.value)}
                             />
                         </div>
                     ))}
                 </div>
             </div>
        )}

        {/* --- INVITE MODAL --- */}
        {showInviteModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowInviteModal(false)}></div>
                <div className="relative w-full max-w-sm bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in duration-200">
                    <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
                        <h3 className="font-bold text-lg dark:text-white">Invite Protege</h3>
                        <button onClick={() => setShowInviteModal(false)}><X className="w-5 h-5 text-slate-400" /></button>
                    </div>
                    
                    <div className="flex border-b border-slate-100 dark:border-slate-700">
                        <button 
                            onClick={() => setInviteMethod('qr')}
                            className={`flex-1 py-3 text-sm font-medium transition-colors ${inviteMethod === 'qr' ? 'text-brand-600 border-b-2 border-brand-600' : 'text-slate-500'}`}
                        >
                            QR Code
                        </button>
                        <button 
                            onClick={() => setInviteMethod('link')}
                            className={`flex-1 py-3 text-sm font-medium transition-colors ${inviteMethod === 'link' ? 'text-brand-600 border-b-2 border-brand-600' : 'text-slate-500'}`}
                        >
                            Link
                        </button>
                        <button 
                            onClick={() => setInviteMethod('email')}
                            className={`flex-1 py-3 text-sm font-medium transition-colors ${inviteMethod === 'email' ? 'text-brand-600 border-b-2 border-brand-600' : 'text-slate-500'}`}
                        >
                            Email
                        </button>
                    </div>

                    <div className="p-6 h-[250px] flex flex-col items-center justify-center">
                        {inviteMethod === 'qr' && (
                            <div className="text-center space-y-3">
                                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm mx-auto w-40 h-40 flex items-center justify-center">
                                    <QrCode className="w-32 h-32 text-slate-800" />
                                </div>
                                <p className="text-xs text-slate-500">Scan to connect instantly.</p>
                            </div>
                        )}
                        {inviteMethod === 'link' && (
                            <div className="w-full space-y-4">
                                <div className="bg-slate-50 dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-700 text-xs break-all text-slate-500 font-mono">
                                    https://dailyreprieve.app/invite/sp_12345
                                </div>
                                <button className="w-full bg-brand-600 text-white py-2 rounded-lg font-bold text-sm flex items-center justify-center gap-2 hover:bg-brand-700">
                                    <LinkIcon className="w-4 h-4" /> Copy Link
                                </button>
                            </div>
                        )}
                        {inviteMethod === 'email' && (
                            <div className="w-full space-y-3">
                                <p className="text-xs text-slate-500 text-center mb-2">Search for an existing user by email.</p>
                                <input 
                                    className="w-full p-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:ring-2 focus:ring-brand-500"
                                    placeholder="friend@example.com"
                                />
                                <button className="w-full bg-brand-600 text-white py-2 rounded-lg font-bold text-sm flex items-center justify-center gap-2 hover:bg-brand-700">
                                    <Mail className="w-4 h-4" /> Search & Invite
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};
